import { debounce, registerComponent } from "helpers";
import WYATTElement from "wyatt";

const MIN_INPUT_LENGTH = 3;
const DEBOUNCE_MS = 300;

/**
 * Google Places Autocomplete as a custom element.
 * Wraps the location input and predictions listbox. Uses a &lt;template&gt;
 * for each prediction row (clone + fill). Dispatches form submit on select.
 * Predictions are restricted to Canada (componentRestrictions.country = "ca").
 * Uses session tokens so keystrokes + place selection bill as one session.
 *
 * Markup: [data-search-input], [data-location-predictions],
 * [data-place-id], template[data-template="prediction-option"].
 */
registerComponent(
  "location-autocomplete",
  class LocationAutocomplete extends WYATTElement {
    constructor() {
      super();

      this.predictions = [];
      this.selectedIndex = -1;
      this.autocompleteService = null;
      this.AutocompleteService = null;
      this.AutocompleteSessionToken = null;
      this.sessionToken = null;
      this.initialized = false;
      this.lastFetchedInput = "";
      this.fetchGeneration = 0;

      this.inputEl = null;
      this.predictionsEl = null;
      this.optionTemplateEl = null;
      this.placeIdInput = null;
      /** @type {{ placeId: string } | null} */
      this.pendingPlaceSelection = null;
      this.requestPredictions = null;

      this.boundHandlers = {
        focus: this.handleFocus.bind(this),
        keydown: this.handleKeydown.bind(this),
        blur: this.handleBlur.bind(this),
        predictionsMouseDown: this.handlePredictionsMouseDown.bind(this),
        submit: this.handleFormSubmit.bind(this),
      };
    }

    /**
     * @type {google.maps.LatLngBounds|null}
     */
    bounds = null;

    connectedCallback() {
      if (this.initialized) {
        return;
      }

      this.inputEl = this.querySelector("[data-search-input]");
      this.predictionsEl = this.querySelector("[data-location-predictions]");
      this.optionTemplateEl = this.querySelector(
        'template[data-template="prediction-option"]',
      );
      this.resolvePlaceIdInput();

      this.init().catch((error) => {
        window.console.log(error);
      });
    }

    /**
     * @param {Object} [options]
     * @param {google.maps.LatLngBounds|null} [options.bounds]
     */
    async init(options = {}) {
      const { bounds } = options;

      if (bounds != null) {
        this.bounds = bounds;
      }

      const { inputEl, predictionsEl } = this;

      const places = await google.maps.importLibrary("places");
      this.AutocompleteService = places.AutocompleteService;
      this.AutocompleteSessionToken = places.AutocompleteSessionToken;
      this.autocompleteService = new this.AutocompleteService();
      this.renewSessionToken();

      predictionsEl.id = `store-search-form__predictions-${this.config.id}`;

      this.requestPredictions = debounce(
        this.fetchPredictions.bind(this),
        DEBOUNCE_MS,
      );

      inputEl.addEventListener("input", (event) => {
        this.selectedIndex = -1;
        this.clearPlaceSelection();

        const { value } = event.target;
        const trimmed = value.trim();

        if (trimmed.length < MIN_INPUT_LENGTH) {
          this.lastFetchedInput = "";
          this.fetchGeneration += 1;
          this.hidePredictions();
          return;
        }

        this.requestPredictions(trimmed);
      });

      inputEl.addEventListener("focus", this.boundHandlers.focus);
      inputEl.addEventListener("keydown", this.boundHandlers.keydown);
      inputEl.addEventListener("blur", this.boundHandlers.blur);
      predictionsEl.addEventListener(
        "mousedown",
        this.boundHandlers.predictionsMouseDown,
      );

      const form = inputEl.closest("form");

      if (form) {
        form.addEventListener("submit", this.boundHandlers.submit, true);
      }

      this.initialized = true;
    }

    resolvePlaceIdInput() {
      this.placeIdInput =
        this.querySelector("[data-place-id]") ??
        this.inputEl?.closest("form")?.querySelector("[data-place-id]") ??
        null;
    }

    renewSessionToken() {
      if (!this.AutocompleteSessionToken) {
        return;
      }

      this.sessionToken = new this.AutocompleteSessionToken();
    }

    consumeSessionToken() {
      const token = this.sessionToken;

      this.renewSessionToken();

      return token;
    }

    takePendingPlaceSelection() {
      const selection = this.pendingPlaceSelection;

      this.pendingPlaceSelection = null;

      return selection;
    }

    clearPlaceSelection() {
      this.pendingPlaceSelection = null;

      if (this.placeIdInput) {
        this.placeIdInput.value = "";
      }
    }

    getPredictionPlaceId(prediction) {
      return prediction.place_id ?? prediction.placeId ?? null;
    }

    fetchPredictions(inputValue) {
      if (inputValue === this.lastFetchedInput) {
        return;
      }

      this.lastFetchedInput = inputValue;
      this.fetchGeneration += 1;
      const generation = this.fetchGeneration;

      const request = {
        input: inputValue,
        types: ["address"],
        componentRestrictions: { country: "ca" },
        sessionToken: this.sessionToken,
      };

      const bounds = this.bounds || WYATT.storeLocator?.googleMapBounds || null;

      if (bounds) {
        request.bounds = bounds;
      }

      this.autocompleteService.getPlacePredictions(
        request,
        (predictions, status) => {
          if (generation !== this.fetchGeneration) {
            return;
          }

          if (
            status !== google.maps.places.PlacesServiceStatus.OK ||
            !predictions
          ) {
            this.predictions = [];
            this.hidePredictions();
            return;
          }

          this.predictions = predictions;
          this.renderPredictions();
          this.showPredictions();
        },
      );
    }

    renderPredictions() {
      const { predictionsEl, optionTemplateEl } = this;

      predictionsEl.replaceChildren();

      this.predictions.forEach((prediction, index) => {
        const fragment = optionTemplateEl.content.cloneNode(true);
        const option = fragment.querySelector("[role='option']");

        option.setAttribute("data-index", String(index));
        option.id = `location-autocomplete-prediction-${this.config.id}-${index}`;

        const mainEl = option.querySelector(
          ".store-search-form__prediction-main",
        );
        const secondaryEl = option.querySelector(
          ".store-search-form__prediction-secondary",
        );

        const { structured_formatting: formatting, description } = prediction;
        const mainText = formatting?.main_text ?? description;
        const secondaryText = formatting?.secondary_text;

        mainEl.textContent = mainText;

        if (secondaryText) {
          secondaryEl.hidden = false;
          secondaryEl.textContent = secondaryText;
        } else {
          secondaryEl.hidden = true;
          secondaryEl.textContent = "";
        }

        option.addEventListener("mousedown", (event) => {
          event.preventDefault();
          this.selectPrediction(prediction);
        });

        predictionsEl.appendChild(fragment);
      });
    }

    selectPrediction(prediction) {
      const { inputEl } = this;
      const { description } = prediction;
      const placeId = this.getPredictionPlaceId(prediction);

      this.resolvePlaceIdInput();

      inputEl.value = description;

      if (placeId) {
        this.pendingPlaceSelection = { placeId };

        if (this.placeIdInput) {
          this.placeIdInput.value = placeId;
        }
      } else {
        this.pendingPlaceSelection = null;
      }

      this.dismissPredictions();
      this.predictions = [];
      this.lastFetchedInput = description.trim();

      const form = inputEl.closest("form");

      if (form) {
        form.dispatchEvent(
          new Event("submit", { bubbles: true, cancelable: true }),
        );
      }
    }

    handleFocus() {
      if (this.predictions.length > 0) {
        this.showPredictions();
      }
    }

    handleKeydown(event) {
      if (this.predictions.length === 0) {
        return;
      }

      switch (event.key) {
        case "ArrowDown":
          event.preventDefault();
          this.selectedIndex = Math.min(
            this.selectedIndex + 1,
            this.predictions.length - 1,
          );
          this.highlightPrediction();
          break;
        case "ArrowUp":
          event.preventDefault();
          this.selectedIndex = Math.max(this.selectedIndex - 1, -1);
          this.highlightPrediction();
          break;
        case "Enter":
          if (this.selectedIndex >= 0 && this.predictions[this.selectedIndex]) {
            event.preventDefault();
            this.selectPrediction(this.predictions[this.selectedIndex]);
          } else if (!this.shouldKeepPredictionsOpen()) {
            this.stopPredictions();
          }
          break;
        case "Escape":
          event.preventDefault();
          this.dismissPredictions();
          break;
        default:
          break;
      }
    }

    handleBlur() {
      setTimeout(() => this.hidePredictions(), 200);
    }

    handlePredictionsMouseDown(event) {
      event.preventDefault();
    }

    handleFormSubmit(event) {
      if (this.shouldKeepPredictionsOpen()) {
        event.preventDefault();
        event.stopImmediatePropagation();

        return;
      }

      this.stopPredictions();
    }

    stopPredictions() {
      this.requestPredictions?.cancel();
      this.fetchGeneration += 1;
      this.predictions = [];
      this.dismissPredictions();
    }

    hasPlaceSelection() {
      return Boolean(
        this.pendingPlaceSelection?.placeId || this.placeIdInput?.value?.trim(),
      );
    }

    shouldKeepPredictionsOpen() {
      return this.config.requirePlaceSelection && !this.hasPlaceSelection();
    }

    dismissPredictions() {
      this.hidePredictions();
      this.selectedIndex = -1;
    }

    highlightPrediction() {
      const { predictionsEl } = this;
      const optionNodes = predictionsEl.querySelectorAll("[role='option']");

      Array.from(optionNodes).forEach((option, i) => {
        const selected = i === this.selectedIndex;
        option.setAttribute("aria-selected", selected ? "true" : "false");
        option.classList.toggle(
          "store-search-form__prediction--selected",
          selected,
        );

        if (selected) {
          option.scrollIntoView({ block: "nearest" });
        }
      });

      if (this.selectedIndex >= 0) {
        predictionsEl.setAttribute(
          "aria-activedescendant",
          `location-autocomplete-prediction-${this.config.id}-${this.selectedIndex}`,
        );
      } else {
        predictionsEl.removeAttribute("aria-activedescendant");
      }
    }

    showPredictions() {
      const { predictionsEl } = this;

      predictionsEl.hidden = false;
      predictionsEl.setAttribute("aria-expanded", "true");
    }

    hidePredictions() {
      const { predictionsEl } = this;

      predictionsEl.hidden = true;
      predictionsEl.setAttribute("aria-expanded", "false");
      predictionsEl.removeAttribute("aria-activedescendant");
    }
  },
);
